"use client";

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Box, VStack, type StackProps } from "@chakra-ui/react";
import { useInfiniteHits } from "react-instantsearch";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import type { GenericEmptyComponentProps } from "@/shared/ui/GenericEmptyComponent";
import {
  MeiliOrigin,
  MeiliSuggestedThought,
  MeiliThought,
} from "@/generated/fastapi";
import { MEILI_SEARCH_CONFIG } from "../config/meiliSearchConfig";
import { Hit } from "meilisearch";
import appSettings from "@/shared/config/appSettings";
import { useSystemDesign } from "@/shared/system-design";
import SearchLoadMoreButton from "./SearchLoadMoreButton";

// NOTE: we do not inclide MeiliSituation in Next/FE
type MeiliDocumentType = MeiliOrigin | MeiliSuggestedThought | MeiliThought;

export type MeiliDocumentSearchHit = {
  objectID: string;
  __position: number;
  __queryID?: string;
} & Hit &
  MeiliDocumentType;

export type BacklogSearchResults = MeiliDocumentSearchHit[];

export type SearchHitRendererProps<
  TDocument extends MeiliDocumentType = MeiliDocumentType,
> = {
  hit: MeiliDocumentSearchHit;
  config: (typeof MEILI_SEARCH_CONFIG)[keyof typeof MEILI_SEARCH_CONFIG];
  onAddAction?: (newItem: MeiliDocumentType) => void;
  onAnchorAction?: (item: MeiliDocumentSearchHit) => void;
  onDeleteSearchResultAction?: (id: string) => void;
  onUpdateSearchResultAction?: (updated: TDocument) => void;
};

interface MyInfiniteHitsGenericProps {
  config: (typeof MEILI_SEARCH_CONFIG)[keyof typeof MEILI_SEARCH_CONFIG];
  renderHit: React.ComponentType<SearchHitRendererProps>;
  indexId?: string;
  onAnchorAction?: (item: MeiliDocumentSearchHit) => void;
  onIndexHitsChange?: (indexId: string, hitIds: string[]) => void;
  onIndexHitsUnmount?: (indexId: string) => void;
  enableVirtualization?: boolean;
  virtualizationThreshold?: number;
  onLoadMoreAction?: () => void;
  emptyContent?: React.ReactNode;
  listGap?: StackProps["gap"];
  emptyState?: Pick<
    GenericEmptyComponentProps,
    "icon" | "iconColor" | "message" | "title" | "variant"
  >;
}

function useVisibleItems(
  hits: BacklogSearchResults,
  enabled: boolean,
): {
  visibleIndices: Set<number>;
  setItemRef: (index: number, element: HTMLDivElement | null) => void;
} {
  const [visibleIndices, setVisibleIndices] = useState<Set<number>>(
    () =>
      new Set(Array.from({ length: Math.min(10, hits.length) }, (_, i) => i)),
  );
  const observerRef = useRef<IntersectionObserver | null>(null);
  const itemRefsRef = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (!enabled || hits.length === 0) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target.getAttribute("data-index"));
          if (entry.isIntersecting) {
            setVisibleIndices((prev) => new Set([...prev, index]));
          }
        });
      },
      {
        rootMargin: appSettings.search.ROOT_MARGIN,
        threshold: appSettings.search.THRESHOLD,
      },
    );

    itemRefsRef.current.forEach((element) => {
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [enabled, hits.length]);

  const setItemRef = useCallback(
    (index: number, element: HTMLDivElement | null) => {
      if (element) {
        itemRefsRef.current.set(index, element);
        observerRef.current?.observe(element);
      } else {
        const existing = itemRefsRef.current.get(index);
        if (existing && observerRef.current) {
          observerRef.current.unobserve(existing);
        }
        itemRefsRef.current.delete(index);
      }
    },
    [],
  );

  return { visibleIndices, setItemRef };
}

export default function MyInfiniteHitsGeneric({
  config,
  renderHit: RenderHit,
  indexId,
  onAnchorAction,
  onIndexHitsChange,
  onIndexHitsUnmount,
  enableVirtualization = true,
  virtualizationThreshold = 15,
  onLoadMoreAction,
  emptyContent,
  listGap,
  emptyState,
}: MyInfiniteHitsGenericProps) {
  const { items, isLastPage, showMore } = useInfiniteHits({
    showPrevious: false,
  });
  const { isMobileDevice } = useSystemDesign();

  const [hits, setHits] = useState<BacklogSearchResults>([]);
  const deletedIdsRef = useRef<Set<string>>(new Set());
  const updatedIdsRef = useRef<Map<string, MeiliDocumentType>>(new Map());

  const handleDeleteSearchResult = useCallback((id: string) => {
    setHits((prev) => prev.filter((h) => h.objectID !== id));
    deletedIdsRef.current.add(id);
  }, []);

  const handleUpdateSearchResult = useCallback((updated: MeiliDocumentType) => {
    setHits((prev) =>
      prev.map((h) =>
        h.objectID === updated.objectID ? { ...h, ...updated } : h,
      ),
    );
    updatedIdsRef.current.set(updated.objectID, updated);
  }, []);

  const handleShowMore = useCallback(() => {
    if (onLoadMoreAction) {
      onLoadMoreAction();
      return;
    }

    showMore();
  }, [onLoadMoreAction, showMore]);

  useEffect(() => {
    const filtered = items
      .filter((hit) => !deletedIdsRef.current.has(hit.objectID))
      .map((hit) =>
        updatedIdsRef.current.has(hit.objectID)
          ? { ...hit, ...updatedIdsRef.current.get(hit.objectID)! }
          : hit,
      ) as BacklogSearchResults;

    setHits(filtered);
  }, [items]);

  useEffect(() => {
    if (indexId && onIndexHitsChange) {
      onIndexHitsChange(
        indexId,
        hits.map((h) => h.objectID),
      );
    }
  }, [hits, indexId, onIndexHitsChange]);

  useEffect(() => {
    return () => {
      if (indexId && onIndexHitsUnmount) {
        onIndexHitsUnmount(indexId);
      }
    };
  }, [indexId, onIndexHitsUnmount]);

  const shouldVirtualize =
    enableVirtualization && hits.length > virtualizationThreshold;

  const { visibleIndices, setItemRef } = useVisibleItems(
    hits,
    shouldVirtualize,
  );

  const renderedHits = useMemo(() => {
    if (!shouldVirtualize) {
      return hits.map((hit) => (
        <RenderHit
          key={hit.objectID}
          hit={hit}
          config={config}
          onAnchorAction={onAnchorAction}
          onDeleteSearchResultAction={handleDeleteSearchResult}
          onUpdateSearchResultAction={handleUpdateSearchResult}
        />
      ));
    }

    return hits.map((hit, index) => {
      const isVisible = visibleIndices.has(index);

      return (
        <Box
          key={hit.objectID}
          ref={(el: HTMLDivElement | null) => setItemRef(index, el)}
          data-index={index}
          minHeight={isVisible ? undefined : "80px"}
        >
          {isVisible ? (
            <RenderHit
              hit={hit}
              config={config}
              onAnchorAction={onAnchorAction}
              onDeleteSearchResultAction={handleDeleteSearchResult}
              onUpdateSearchResultAction={handleUpdateSearchResult}
            />
          ) : null}
        </Box>
      );
    });
  }, [
    hits,
    shouldVirtualize,
    visibleIndices,
    config,
    RenderHit,
    handleDeleteSearchResult,
    handleUpdateSearchResult,
    onAnchorAction,
    setItemRef,
  ]);

  if (hits.length === 0) {
    if (emptyContent) {
      return emptyContent;
    }

    return (
      <GenericEmptyComponent
        title={emptyState?.title ?? "No results."}
        message={emptyState?.message ?? "Adjust criteria or add records."}
        variant={emptyState?.variant ?? "minimal"}
        icon={emptyState?.icon ?? "info"}
        iconColor={emptyState?.iconColor}
        compensateForCheckboxSpace={!isMobileDevice}
      />
    );
  }

  return (
    <Box>
      <VStack align="stretch" gap={listGap}>
        {renderedHits}
      </VStack>
      {!isLastPage && <SearchLoadMoreButton onClick={handleShowMore} />}
    </Box>
  );
}
